import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  SvgIcon,
} from '@mui/material';
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCloseLine,
  RiMenuLine,
} from '@remixicon/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import IotsHeaderLogo from '../../components/IotsHeaderLogo';
import LanguageSwitcher, {
  MobileLanguageSwitcher,
} from '../../components/LanguageSwitch';
import { useCurrentUser } from '../../contexts/currentUser';
import { useLanguage } from '../../contexts/language';
import { Role } from '../../types/user';
import AccountMenu, { MobileAccountMenu } from './AccountMenu';

interface NavigationLink {
  url: string;
  label: string;
  isExact?: boolean;
}

interface NavigationLinkGroup {
  url: null;
  label: string;
  items: NavigationLink[];
}

type NavigationItem = NavigationLink | NavigationLinkGroup;

const NavigationLinkElement = ({
  link,
  className = '',
  isLinkActive,
}: {
  link: NavigationLink;
  className?: string;
  isLinkActive: (link: NavigationLink) => boolean;
}) => {
  return (
    <NavLink
      className={[className, isLinkActive(link) ? 'font-bold' : '']
        .filter(Boolean)
        .join(' ')}
      to={link.url}
    >
      {link.label}
    </NavLink>
  );
};

const NavigationLinkGroupElement = ({
  group,
  isLinkActive,
}: {
  group: NavigationLinkGroup;
  isLinkActive: (link: NavigationLink) => boolean;
}) => {
  const { label, items } = group;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isGroupActive = useMemo(
    () => items.length && items.some(link => isLinkActive(link)),
    [isLinkActive, items],
  );

  const closeMenu = () => setAnchorEl(null);

  const isOpen = Boolean(anchorEl);

  return (
    <>
      <button
        onClick={event => {
          setAnchorEl(event.currentTarget as HTMLElement);
        }}
        className="whitespace-nowrap flex items-center"
      >
        <span className={isGroupActive ? 'font-bold' : undefined}>{label}</span>
        <SvgIcon component={anchorEl ? RiArrowUpSLine : RiArrowDownSLine} />
      </button>
      <Menu
        /* It seems there is some issue with the Menu elements not removing the backdrop element when they close.
        It is not trivial to reproduce, but it will happen sometimes, making the page effectively blocked when changing to another page. This key will ensure the backdrop is always removed when the menu closes. 
         */
        key={isOpen.toString()}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
      >
        {items.map(link => (
          <MenuItem key={link.url} onClick={closeMenu}>
            <NavigationLinkElement
              isLinkActive={isLinkActive}
              className="w-full"
              link={link}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const MobileNavigationLinkGroupElement = ({
  group,
  isLinkActive,
  className,
}: {
  group: NavigationLinkGroup;
  isLinkActive: (link: NavigationLink) => boolean;
  className?: string;
}) => {
  const { label, items } = group;

  const isGroupActive = useMemo(
    () => items.length && items.some(link => isLinkActive(link)),
    [isLinkActive, items],
  );

  return (
    <Accordion
      variant="outlined"
      className={[
        className,
        'm-0 bg-iots-brand text-iots-brand-inverted border-0',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <AccordionSummary
        className="p-0 min-h-0 justify-start gap-2 w-auto"
        slotProps={{
          content: { className: 'm-0 p-0 min-h-0 flex-[0_0_auto]' },
        }}
        expandIcon={
          <SvgIcon
            component={RiArrowDownSLine}
            className="fill-iots-brand-inverted"
          />
        }
      >
        <span className={isGroupActive ? 'font-bold' : undefined}>{label}</span>
      </AccordionSummary>
      <AccordionDetails className="p-0 pt-4 min-h-0 flex flex-col gap-4 justify-start">
        {items.map(link => (
          <div key={link.url}>
            <NavigationLinkElement
              isLinkActive={isLinkActive}
              className="text-sm"
              link={link}
            />
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const AdminHeader = () => {
  const { t, language } = useLanguage();
  const { hasRole, user } = useCurrentUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isLinkActive = useCallback(
    (link: NavigationLink) =>
      link.isExact ? pathname === link.url : pathname.startsWith(link.url),
    [pathname],
  );

  const navigationItems = useMemo<NavigationItem[]>(() => {
    const items: NavigationItem[] = [];
    items.push({
      url: '/admin',
      label: t('content.dashboard'),
      isExact: true,
    });

    items.push(
      {
        url: '/admin/journeys',
        label: t('content.journeys'),
      },
      { url: '/admin/invoices', label: t('content.invoices') },
      { url: '/admin/contracts', label: t('content.contracts') },
      {
        url: null,
        label: t('content.fleet'),
        items: [
          { url: '/admin/vessels', label: t('content.vessels') },
          { url: '/admin/crew', label: t('content.crew') },
        ],
      },
    );

    if (hasRole(Role.DRCD, Role.ADMIN)) {
      items.push({
        url: '/admin/analytics',
        label: t('content.analytics'),
        isExact: false,
      });

      const managementItem: NavigationLinkGroup = {
        url: null,
        label: t('content.management'),
        items: [{ url: '/admin/operators', label: t('content.operators') }],
      };

      if (hasRole(Role.ADMIN)) {
        managementItem.items.push(
          { url: '/admin/users', label: t('content.users') },
          { url: '/admin/islands', label: t('content.islands') },
        );
      }

      items.push(managementItem);
    }

    return items;
  }, [hasRole, t]);

  // Close the mobile menu if the window resizes
  useEffect(() => {
    const handleResize = () => {
      setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close the mobile menu if the location or language changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname, language]);

  return (
    <header className="bg-iots-brand text-iots-brand-inverted overflow-hidden">
      <div className="max-w-[100vw]">
        <div className="main-content hidden lg:grid grid-cols-[1fr_auto_auto] items-center py-2">
          <div className="flex items-center gap-8 mr-4">
            <IotsHeaderLogo url="/admin" />
            <nav className="flex items-center gap-4">
              {navigationItems.map(item =>
                item.url !== null ? (
                  <NavigationLinkElement
                    isLinkActive={isLinkActive}
                    key={`link-${item.label}-${item.url}`}
                    link={item}
                  />
                ) : (
                  <NavigationLinkGroupElement
                    isLinkActive={isLinkActive}
                    key={`group-${item.label}`}
                    group={item}
                  />
                ),
              )}
            </nav>
          </div>
          <div className="flex items-center">
            <LanguageSwitcher />
          </div>
          <AccountMenu />
        </div>
      </div>
      <div className="max-w-[100vw]">
        <div className="main-content lg:hidden grid grid-cols-[1fr_auto] items-center py-2">
          <IotsHeaderLogo url="/admin" />
          <IconButton onClick={() => setIsMobileMenuOpen(true)}>
            <SvgIcon
              className="fill-iots-brand-inverted"
              component={RiMenuLine}
            />
          </IconButton>
        </div>
      </div>
      <Drawer
        anchor="right"
        open={isMobileMenuOpen}
        slotProps={{ paper: { className: 'w-full bottom-0' } }}
      >
        <div className="bg-iots-brand text-iots-brand-inverted absolute top-0 bottom-0 left-0 right-0 overflow-hidden px-4 grid grid-rows-[min-content_min-content_1fr_min-content]">
          {/* The next content ensures the header heigth the same for the margin spacing and the correct position of the close button  */}
          <div className="invisible grid grid-cols-[1fr_auto] items-center py-2">
            <IotsHeaderLogo url="/admin" />
            <IconButton
              className="visible"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <SvgIcon
                className="fill-iots-brand-inverted"
                component={RiCloseLine}
              />
            </IconButton>
          </div>
          <div className="mb-9">
            <header className="text-2xl/[40px] font-bold">{`${user.firstName} ${user.lastName}`}</header>
            <MobileAccountMenu />
          </div>
          <div className="overflow-auto">
            <nav className="inline-flex flex-col gap-4">
              {navigationItems.map(item =>
                item.url !== null ? (
                  <NavigationLinkElement
                    isLinkActive={isLinkActive}
                    key={`link-${item.label}-${item.url}`}
                    link={item}
                  />
                ) : (
                  <MobileNavigationLinkGroupElement
                    isLinkActive={isLinkActive}
                    key={`group-${item.label}`}
                    group={item}
                  />
                ),
              )}
            </nav>
          </div>
          <div className="mt-4 mb-14">
            <MobileLanguageSwitcher />
          </div>
        </div>
      </Drawer>
    </header>
  );
};

export default AdminHeader;
